import {
  ActionReducerMapBuilder,
  EntityState,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';

import { DashboardService, Widget } from '@app/gen/dashboard';
import { ThunkExtra } from '@app/store';
import { DynamicEntity } from '@app/types';

import { DashboardState } from '../../dashboard.state';

export const getWidgetList = createAsyncThunk<Widget[], undefined, ThunkExtra>(
  'widgets/getWidgetList',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const result =
        await inject(DashboardService).getUserWidgetMainLayoutSettings();
      return result.widgets;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const changeUserWidget = createAsyncThunk<Widget, Widget, ThunkExtra>(
  'widgets/changeUserWidget',
  async (widget, { rejectWithValue, dispatch, extra: { inject } }) => {
    try {
      const dashboardService = inject(DashboardService);

      await dashboardService.updateUserWidgetActiveState({
        body: {
          is_active: widget.is_active,
        },
        params: { widget_id: widget.id },
      });

      return widget;
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: `Не удалось ${widget.is_active ? 'включить' : 'выключить'} виджет`,
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const widgetsReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<DynamicEntity, string> & DashboardState
  >,
) => {
  builder.addCase(changeUserWidget.pending, (state, { meta }) => {
    state.widgetsSettings.widgets = state.widgetsSettings.widgets.map(
      (widget) => {
        if (widget.id === meta.arg.id) {
          return { ...widget, is_active: !!meta.arg.is_active };
        }

        return widget;
      },
    );
  });

  builder.addCase(changeUserWidget.rejected, (state, { meta }) => {
    state.widgetsSettings.widgets = state.widgetsSettings.widgets.map(
      (widget) => {
        if (widget.id === meta.arg.id) {
          return { ...widget, is_active: !meta.arg.is_active };
        }

        return widget;
      },
    );
  });

  builder.addCase(getWidgetList.rejected, (state, { payload, error }) => {
    state.widgetsSettings.status = 'failed';
    state.widgetsSettings.error =
      payload ||
      ({
        info: (error && error.message) || 'Что-то пошло не так',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
  builder.addCase(getWidgetList.fulfilled, (state, { payload }) => {
    state.widgetsSettings.widgets = payload;
    state.widgetsSettings.status = 'complete';
  });
  builder.addCase(getWidgetList.pending, (state) => {
    state.widgetsSettings.status = 'loading';
  });
};
